import React, { useEffect, useState, useRef } from "react";
import API from "../../utils/API";
import Swal from "sweetalert2";

function Users() {
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [openMenuId, setOpenMenuId] = useState(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!localStorage.getItem('yc_usr_dt_ls')) {
            window.location.href = '/login';
        }
        Swal.fire({
            title: "Carregando...",
            showConfirmButton: false,
            allowOutsideClick: false,
            willOpen: () => Swal.showLoading(),
        });
        API.getUsers(API.cancelToken())
            .then((res) => {
                setUsers(res.data);
                Swal.close();
            })
            .catch((err) => {
                console.error(err);
                Swal.close();
                Swal.fire("Erro", "Erro ao carregar usuários", "error");
            });
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpenMenuId(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredUsers = users.filter(user =>
        Object.values(user).some(val =>
            String(val).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const getStatusBadge = (status) => {
        if (status === 1) return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Ativo</span>;
        if (status === 0) return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pendente</span>;
        if (status === -1) return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Inativo</span>;
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    };

    const getRoleBadge = (role) => {
        const colors = {
            ADMIN: "bg-purple-100 text-purple-800",
            MECANICO: "bg-blue-100 text-blue-800",
            COMPRADOR: "bg-orange-100 text-orange-800",
            RECEPCAO: "bg-teal-100 text-teal-800",
            BOSS: "bg-indigo-100 text-indigo-800",
        };
        const cls = colors[role] || "bg-gray-100 text-gray-800";
        return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>{role}</span>;
    };

    const handleResetPassword = (user) => {
        setOpenMenuId(null);
        Swal.fire({
            title: "Resetar senha?",
            text: `Uma nova senha será gerada e enviada para ${user.email}.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Resetar",
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: "Resetando senha...",
                    allowOutsideClick: false,
                    didOpen: () => Swal.showLoading(),
                });
                API.resetUserPassword(API.cancelToken(), user.user_id)
                    .then(() => {
                        Swal.fire("Sucesso", "Nova senha enviada por email.", "success");
                    })
                    .catch((err) => {
                        console.error(err);
                        Swal.fire("Erro", "Erro ao resetar senha.", "error");
                    });
            }
        });
    };

    const handleDeleteUser = (user) => {
        setOpenMenuId(null);
        Swal.fire({
            title: "Excluir usuário?",
            text: `Tem certeza que deseja excluir ${user.first_name} ${user.last_name}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Excluir",
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (result.isConfirmed) {
                API.deleteUser(API.cancelToken(), user.user_id)
                    .then(() => {
                        setUsers(prev => prev.filter(u => u.user_id !== user.user_id));
                        Swal.fire("Excluído", "Usuário excluído com sucesso.", "success");
                    })
                    .catch((err) => {
                        console.error(err);
                        Swal.fire("Erro", "Erro ao excluir usuário.", "error");
                    });
            }
        });
    };

    return (
        <div className="flex w-full h-full pt-8 px-20 pb-24 items-center gap-8 self-stretch max-md:px-4">
            <div className="flex w-full h-auto py-6 px-11 flex-col items-center gap-6 self-stretch bg-white rounded-lg shadow max-md:px-4">
                {/* Header */}
                <div className="flex w-full justify-between items-center flex-wrap gap-4">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Usuários</h1>
                        <p className="text-sm text-gray-500 mt-1">{users.length} usuário{users.length !== 1 ? 's' : ''} cadastrado{users.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="relative">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                            </svg>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Pesquisar..."
                                className="border border-gray-300 rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#389E0D] focus:border-transparent w-64"
                            />
                        </div>
                        <button
                            onClick={() => window.location.href = "/manage/user"}
                            className="flex items-center gap-2 bg-[#389E0D] text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                            </svg>
                            Novo Usuário
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="w-full overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Usuário</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Cargo</th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map((user) => (
                                    <tr key={user.user_id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-shrink-0 h-9 w-9 rounded-full bg-gray-200 flex items-center justify-center text-sm font-semibold text-gray-600">
                                                    {user.first_name?.[0]}{user.last_name?.[0]}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{user.first_name} {user.last_name}</p>
                                                    <p className="text-xs text-gray-400">ID {user.user_id}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                                        <td className="px-4 py-3">{getRoleBadge(user.role)}</td>
                                        <td className="px-4 py-3">{getStatusBadge(user.status)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="relative inline-block text-left" ref={openMenuId === user.user_id ? menuRef : null}>
                                                <button
                                                    onClick={() => setOpenMenuId(openMenuId === user.user_id ? null : user.user_id)}
                                                    className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                                                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                                    </svg>
                                                </button>

                                                {openMenuId === user.user_id && (
                                                    <div className="origin-top-right absolute right-0 mt-1 w-48 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 py-1">
                                                        <button
                                                            onClick={() => { setOpenMenuId(null); window.location.href = `/manage/user?id=${user.user_id}`; }}
                                                            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                            </svg>
                                                            Editar
                                                        </button>
                                                        <button
                                                            onClick={() => handleResetPassword(user)}
                                                            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                                            </svg>
                                                            Resetar Senha
                                                        </button>
                                                        <div className="border-t border-gray-100 my-1"></div>
                                                        <button
                                                            onClick={() => handleDeleteUser(user)}
                                                            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                            </svg>
                                                            Excluir
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center py-8 text-gray-400 text-sm">Nenhum usuário encontrado.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default Users;
